import { useMemo, useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import TempDropDown from "./TempDropDown";
import { Chart } from 'react-chartjs-2';
import { graphNudgeWisePerformance, selectNudgeWisePerformanceData, selectNudgeWisePerformanceWeeks, selectNudgeWisePerformanceStatus } from "../../../layout/Persona/components/NudgeBasedPerformance/nudgeCouponSlice";
import "../graphStyles.scss";

const InfoIcon = ({ tooltipText }) => (
  <span className="info-tooltip-container">
    <svg className="info-tooltip-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="16" x2="12" y2="12" />
      <line x1="12" y1="8" x2="12.01" y2="8" />
    </svg>
    <span className="info-tooltip-bubble">{tooltipText}</span>
  </span>
);

const calcPercentage = (total, obtain) => {
  if (!total || total <= 0) return 0;
  return Math.round((obtain * 100) / total);
};

const safePercent = (total, value) => {
  if (!total || total <= 0) return 0;
  return Number(((value / total) * 100).toFixed(2));
};

export default function NudgeWisePerformance({ user }) {
  const dispatch = useDispatch();

  // Redux selectors for nudge-wise performance data
  const nudgeWiseDataMap = useSelector(selectNudgeWisePerformanceData);
  const nudgeWeeks = useSelector(selectNudgeWisePerformanceWeeks);
  const nudgeLoading = useSelector(selectNudgeWisePerformanceStatus) === "loading";

  // State for current nudge week selection
  const [currentNudgeWeek, setCurrentNudgeWeek] = useState("");

  // Fetch nudge-wise performance data from Redux
  useEffect(() => {
    if (user) {
      dispatch(graphNudgeWisePerformance(user));
    }
  }, [user, dispatch]);

  // Initialize currentNudgeWeek when nudgeWeeks data is available
  useEffect(() => {
    if (nudgeWeeks && nudgeWeeks.length > 0) {
      setCurrentNudgeWeek((prev) =>
        prev && nudgeWiseDataMap[prev] ? prev : nudgeWeeks[0]
      );
    }
  }, [nudgeWeeks, nudgeWiseDataMap]);

  // Filter and prepare data for display
  const safeNudgeWeekData = useMemo(() => {
    const currentData = nudgeWiseDataMap[currentNudgeWeek];
    return Array.isArray(currentData) ? currentData : [];
  }, [currentNudgeWeek, nudgeWiseDataMap]);

  const filteredNudgeWeekData = useMemo(() => {
    return safeNudgeWeekData.filter((nudge) => Number(nudge.couponShown ?? 0) > 1);
  }, [safeNudgeWeekData]);

  // Chart configuration
  const nudgeChartLabels = filteredNudgeWeekData?.map(
    (item) => item.nudgeName || item.couponCode || "Nudge"
  );

  const nudgeChartData = {
    labels: nudgeChartLabels,
    datasets: [
      {
        label: "Hypotheses Without Coupon Add to cart %",
        data: filteredNudgeWeekData?.map((item) =>
          safePercent(
            item.eligibleButNotShownUsers ?? 0,
            item.eligibleButNotShownUsersAddToCart ?? 0
          )
        ),
        backgroundColor: "rgba(80, 180, 140, 1)",
        type: "bar",
      },
      {
        label: "Null Hypotheses Add to cart %",
        data: filteredNudgeWeekData?.map((item) =>
          safePercent(item.ineligibleUsers ?? 0, item.ineligibleUsersAddToCart ?? 0)
        ),
        backgroundColor: "rgba(120, 120, 120, 1)",
        type: "bar",
      },
      {
        label: "Coupon Shown Add to cart %",
        data: filteredNudgeWeekData?.map((item) =>
          safePercent(item.couponShown ?? 0, item.shownUsersAddToCart ?? 0)
        ),
        backgroundColor: "rgba(100, 100, 250, 1)",
        type: "bar",
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "right",
      },
      tooltip: {
        callbacks: {
          label: function (context) {
            return `${context.dataset.label}: ${context.raw}%`;
          },
        },
      },
    },
    scales: {
      x: {
        title: {
          display: true,
          text: "Nudge",
        },
        grid: {
          display: false,
        },
      },
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: "Add to Cart Rate (%)",
        },
      },
    },
  };

  return (
    <>
      {!nudgeLoading && filteredNudgeWeekData?.length > 0 && (
        <>
          <div className="graph flex flex-col">
            <div className="graph__title">
              <h4 className="flex items-center gap-1.5">
                <span>A/B test performance report</span>
                <InfoIcon tooltipText="Compares add to cart rates between users with coupons shown vs null hypotheses/control groups." />
              </h4>
              <TempDropDown
                currentWeek={currentNudgeWeek}
                setCurrentWeek={setCurrentNudgeWeek}
                weeks={nudgeWeeks}
              />
            </div>

            <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
              <table className="min-w-full border-collapse text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th
                      rowSpan="2"
                      className="border border-gray-200 px-4 py-3 text-left align-middle font-semibold text-gray-900"
                    >
                      Nudge name
                    </th>
                    <th
                      rowSpan="2"
                      className="border border-gray-200 px-4 py-3 text-left align-middle font-semibold text-gray-900"
                    >
                      Coupon code
                    </th>

                    {[
                      "Hypotheses Without Coupon",
                      "Null Hypotheses",
                      "Coupon Shown",
                    ].map((head) => (
                      <th
                        key={head}
                        colSpan="2"
                        className="border border-gray-200 px-4 py-3 text-center font-semibold text-gray-900"
                      >
                        {head}
                      </th>
                    ))}
                  </tr>

                  <tr>
                    {[
                      "User",
                      "Add to cart",
                      "User",
                      "Add to cart",
                      "User",
                      "Add to cart",
                    ].map((head, index) => (
                      <th
                        key={index}
                        className="border border-gray-200 px-4 py-2 text-center text-xs font-semibold text-gray-700"
                      >
                        {head}
                      </th>
                    ))}
                  </tr>
                </thead>

                <tbody className="bg-white">
                  {filteredNudgeWeekData.map((nudge, index) => {
                    const withoutCouponUsers =
                      (nudge.eligibleUsers ?? 0) - (nudge.couponShown ?? 0);
                    const withoutCouponATC =
                      (nudge.eligibleUsersAddToCart ?? 0) -
                      (nudge.shownUsersAddToCart ?? 0);

                    const nullUsers = nudge.ineligibleUsers ?? 0;
                    const nullATC = nudge.ineligibleUsersAddToCart ?? 0;

                    const couponShownUsers = nudge.couponShown ?? 0;
                    const couponShownATC = nudge.shownUsersAddToCart ?? 0;

                    const ATCCell = ({ users, atc }) => (
                      <div className="flex items-center justify-center gap-2">
                        <span className="text-gray-700">{atc}</span>
                        <span className="inline-flex min-w-[44px] justify-center rounded-full bg-blue-100 px-2 py-1 text-xs font-medium text-blue-800">
                          {calcPercentage(users, atc)}%
                        </span>
                      </div>
                    );

                    return (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="whitespace-nowrap border border-gray-200 px-4 py-4 text-sm font-medium text-gray-900">
                          {nudge.nudgeName || ""}
                        </td>

                        <td className="whitespace-nowrap border border-gray-200 px-4 py-4 text-sm font-medium text-gray-900">
                          {nudge.couponCode || ""}
                        </td>

                        <td className="whitespace-nowrap border border-gray-200 px-4 py-4 text-center text-sm text-gray-600">
                          {withoutCouponUsers}
                        </td>
                        <td className="whitespace-nowrap border border-gray-200 px-4 py-4 text-sm text-gray-600">
                          <ATCCell users={withoutCouponUsers} atc={withoutCouponATC} />
                        </td>

                        <td className="whitespace-nowrap border border-gray-200 px-4 py-4 text-center text-sm text-gray-600">
                          {nullUsers}
                        </td>
                        <td className="whitespace-nowrap border border-gray-200 px-4 py-4 text-sm text-gray-600">
                          <ATCCell users={nullUsers} atc={nullATC} />
                        </td>

                        <td className="whitespace-nowrap border border-gray-200 px-4 py-4 text-center text-sm text-gray-600">
                          {couponShownUsers}
                        </td>
                        <td className="whitespace-nowrap border border-gray-200 px-4 py-4 text-sm text-gray-600">
                          <ATCCell users={couponShownUsers} atc={couponShownATC} />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          <div className="graph flex flex-col col-span-2">
            <div className="graph__title">
              <h4 className="flex items-center gap-1.5">
                <span>Nudge Add to Cart Rate Comparison</span>
                <InfoIcon tooltipText="Visualizes rates of coupon additions to cart across different hypotheses." />
              </h4>
              <TempDropDown
                currentWeek={currentNudgeWeek}
                setCurrentWeek={setCurrentNudgeWeek}
                weeks={nudgeWeeks}
              />
            </div>

            <Chart
              className="chart"
              id="nudge-conversion-chart"
              options={options}
              data={nudgeChartData}
            />
          </div>
        </>
      )}
    </>
  );
}
